import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { storefrontFetch } from '../misc/fetch_helpers'
import RentalPlan, { correctRentalPlan } from '../helpers/rental_plan'
import { objectToCamelCase, objectToSnakeCase } from '../misc/object_case_helpers'
import CartLoader from './cart_loader'
import Cart from './cart'

class RentalCart extends Component {
  constructor() {
    super()
    this.state = {
      rentalPlan: null,
      updating: true,
      error: null,
      loading: true
    }

    this.updateRentalPlan = this.updateRentalPlan.bind(this)
  }

  getInitialPlan = () => {
    storefrontFetch('/cart.json')
      .then(data => {
        const initialPlan = new RentalPlan(objectToCamelCase(data))
        const corrected = correctRentalPlan(initialPlan)

        this.setState({
          rentalPlan: corrected.rentalPlan,
          updating: false,
          // tell the user
          dateCorrectionMessage: corrected.message,
          // stop showing the spinner
          loading: false
        })

        if (corrected.message) {
        // additionally, behave as though the user selected this new plan
          this.updateRentalPlan(corrected.rentalPlan)
        }
      })
      .catch(
        error => {
          if (process.env.NODE_ENV === 'development') {
            console.log(error)
          }
          this.setState({
            error: 'Uh oh, unable to get your cart.',
            loading: false
          })
        }
      )
  }

  componentDidMount() {
    this.getInitialPlan()
  }

  updateRentalPlan(newRentalPlan) {
    // This function provides immediate, optimistic updating of the local plan while
    // asynchronously updating the server's plan.

    // This requires calling setState({plan:}) twice: once with the new client-side
    // plan C, and a second time (after the fetch() completes) with the new
    // server-side plan S. Shown graphically:
    //     C ------ S
    //     time->

    // Here we wish this.state.rentalPlan to go from C to S.
    // A problem occurs when this function is called more than once. Requests can
    // overlap, and the fetch() requests may complete out of order:
    //   C ------ S
    //     C' ------------- S'
    //       C'' ------ S''
    //   time->

    // We wish the state to go C -> C' -> C'' -> S'', rather than naïvely setting
    // state C -> C' -> C'' -> S -> S'' -> S'. This requires us to identify S and
    // S' so we can avoid calling setState(). Fortunately, we have a simple
    // solution: each time we consider a new server-side state S, determine if
    // the current state is still the corresponding state C. This gives us:
    //   C ------ .
    //     C' ------------- .
    //       C'' ------ S''
    //   time->

    // Ensure this condition by checking that the object identity of this.state.rentalPlan
    // is exactly equal to the object identity of the original optimistic assignment.

    if (!(newRentalPlan instanceof RentalPlan)) throw new Error('must pass a RentalPlan')

    const originalPlan = this.state.rentalPlan
    const corrected = correctRentalPlan(newRentalPlan)

    this.setState({
      rentalPlan: corrected.rentalPlan,
      updating: true,
      dateCorrectionMessage: corrected.message
    })

    const finish = (finalPlan) => {
      this.setState(state => {
        if (state.rentalPlan === corrected.rentalPlan) {
          // this should be using setSessionData from the useSession hoo, but this would all need to be refactored to be a function component
          const itemCount = corrected.rentalPlan.cart.reduce((accumulator, item) => accumulator + item.quantity, 0)
          const sessionData = window.sessionDataService.get()
          window.sessionDataService.set(JSON.stringify({ ...sessionData, cart: { ...sessionData.cart, size: itemCount } }))
          // we were the last ones to touch state, replace the plan in the state with finalPlan and signal completion
          return { rentalPlan: new RentalPlan(finalPlan), updating: false }
        } else {
          // someone else changed state, avoid clobbering it
          return {}
        }
      })
    }

    storefrontFetch('/cart.json', {
      method: 'put',
      // transform the data to snake_case to send it to the server
      body: objectToSnakeCase(corrected.rentalPlan)
    })
      .then(newServerSidePlan => {
      // put succeeded, make the server side plan authoritative
      // need to transform server response to camelCase to instantiate new RentalPlan
        finish(objectToCamelCase(newServerSidePlan))
      }, (error) => {
      // put failed, roll it back to the previous plan
        if (process.env.NODE_ENV === 'development') {
          console.log(error)
        }
        finish(originalPlan)
      })
  }

  render() {
    return (
      <CartLoader
        loading={this.state.loading}
        error={{
          message: this.state.error,
          button: 'default'
        }}
      >
        {
          (this.props.isActive && !(this.state.loading || this.state.error))
            ? <Cart
                cartPlan={this.state.rentalPlan}
                updateCartPlan={this.updateRentalPlan}
                updating={this.state.updating}
                dateCorrectionMessage={this.state.dateCorrectionMessage}
              />
            : ''
        }
      </CartLoader>
    )
  }
}

RentalCart.propTypes = {
  isActive: PropTypes.bool.isRequired
}

export default RentalCart
